/**
 * Entity Document Browser Component - Fully Migrated to React Query
 * Handles document browsing for specific entities (fund, investor, portfolio company)
 */

import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import {
  Upload,
  Search,
  FileText,
  Download,
  Trash2,
  Folder,
  Plus,
  X,
  Loader2,
  MoreVertical,
  ArrowLeft,
  ChevronRight,
  Tag,
  Eye,
  EyeOff,
  CalendarDays,
} from 'lucide-react';
import { formatDate, formatFileSize } from '@/utils/formatters';
import { Document, CollectionType, EntityType } from '@/types/document';
import { DocumentViewer } from '@/components/documents/DocumentViewer';
import { DocumentPreview } from '@/components/documents/DocumentPreview';
import { DocumentTagsList } from '@/components/documents/DocumentTagBadge';
import { DocumentTagFilter } from '@/components/documents/DocumentTagFilter';
import { DocumentTagPickerSimple } from '@/components/documents/DocumentTagPickerSimple';
import { InvestorVisibilityUploadDialog } from '@/components/documents/browser/InvestorVisibilityUploadDialog';
import { EditAsOfDialog } from '@/components/documents/browser/EditAsOfDialog';
import { getInvestorVisibilityLabels } from '@/components/documents/investorVisibilityLabels';

// React Query hooks
import {
  useEntityCollections,
  useEntityCollectionContents,
  useEntityFolder,
  useCreateEntityCollection,
  useDeleteCollection,
  useDeleteDocument,
  useUploadMultipleDocuments,
  useDownloadDocument,
  useSearchDocuments,
  useUpdateDocumentInvestorVisibility,
  useBulkUpdateInvestorVisibility,
  type BulkUploadError,
} from '@/hooks/react-query/useDocuments';
import { useUpdateDocumentTags } from '@/hooks/react-query/useDocumentTags';

interface EntityDocumentBrowserProps {
  entityType: EntityType;
  entityId: string;
  entityName: string;
  title?: string;
  description?: string;
  showUpload?: boolean;
  showSearch?: boolean;
  showCreateFolder?: boolean;
  showHeader?: boolean;
  showActions?: boolean;
  className?: string;
  onUpload?: (files: FileList) => void;
  onCreateFolder?: () => void;
}

export function EntityDocumentBrowser({
  entityType,
  entityId,
  entityName,
  title,
  description,
  showUpload = true,
  showSearch = true,
  showCreateFolder = true,
  showHeader = true,
  showActions = true,
  className = '',
  onUpload,
  onCreateFolder,
}: EntityDocumentBrowserProps) {
  const { toast } = useToast();
  const visibilityLabels = getInvestorVisibilityLabels(entityType);

  // State
  const [currentCollectionId, setCurrentCollectionId] = useState<string | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [tagFilterMode, setTagFilterMode] = useState<'any' | 'all'>('any');
  const [editingDocumentTags, setEditingDocumentTags] =
    useState<Document | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );

  // Modals
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [showDeleteFolderDialog, setShowDeleteFolderDialog] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showDeleteDocumentDialog, setShowDeleteDocumentDialog] =
    useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
    null
  );
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  );
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  // Upload settings
  const [uploadInvestorVisible, setUploadInvestorVisible] = useState(false);
  const [uploadAsOf, setUploadAsOf] = useState('');
  const [editingAsOfDocument, setEditingAsOfDocument] =
    useState<Document | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  // Per-file rejection reasons from the last upload attempt, keyed by file name,
  // so the user can drop the rejected files and retry without starting over.
  const [uploadFileErrors, setUploadFileErrors] = useState<
    Record<string, string>
  >({});

  // Bulk selection
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(
    () => new Set()
  );

  // React Query hooks
  const { data: entityFolder } = useEntityFolder(entityType, entityId);
  const { isLoading: isLoadingCollections } = useEntityCollections(
    entityType,
    entityId
  );
  const { data: collectionContents, isLoading: isLoadingContents } =
    useEntityCollectionContents(
      entityType,
      entityId,
      currentCollectionId || entityFolder?.folder_id || '',
      { enabled: !!(currentCollectionId || entityFolder?.folder_id) }
    );

  const { data: searchResults, isLoading: isSearching } = useSearchDocuments(
    {
      query: searchQuery,
      entity_type: entityType,
      entity_id: entityId,
    },
    { enabled: !!searchQuery }
  );

  // Mutations
  const createCollectionMutation = useCreateEntityCollection();
  const deleteCollectionMutation = useDeleteCollection();
  const deleteDocumentMutation = useDeleteDocument();
  const uploadMultipleDocumentsMutation = useUploadMultipleDocuments();
  const downloadDocumentMutation = useDownloadDocument();
  const updateTagsMutation = useUpdateDocumentTags();
  const updateInvestorVisibilityMutation =
    useUpdateDocumentInvestorVisibility();
  const bulkUpdateVisibilityMutation = useBulkUpdateInvestorVisibility();

  // Computed values
  const loading = isLoadingCollections || isLoadingContents || isSearching;
  const allDocuments = searchQuery
    ? searchResults?.documents || []
    : collectionContents?.documents || [];
  const subcollections = collectionContents?.subcollections || [];
  const currentCollection = collectionContents?.collection;

  // Check if we're in a subfolder (not at entity root)
  const isInSubfolder =
    currentCollectionId && currentCollectionId !== entityFolder?.folder_id;

  // Filter documents by tags
  const documents = allDocuments.filter((doc) => {
    if (filterTags.length === 0) return true;

    if (tagFilterMode === 'all') {
      // Document must have all selected tags
      return filterTags.every((tag) => doc.tags.includes(tag));
    } else {
      // Document must have at least one selected tag
      return filterTags.some((tag) => doc.tags.includes(tag));
    }
  });
  const hasItems = subcollections.length > 0 || documents.length > 0;

  // Navigation
  const navigateToCollection = (collectionId: string) => {
    if (collectionId === 'root') {
      setCurrentCollectionId(entityFolder?.folder_id || null);
    } else {
      setCurrentCollectionId(collectionId);
    }
  };

  const navigateUp = () => {
    if (currentCollection?.parent_collection_id) {
      navigateToCollection(currentCollection.parent_collection_id);
    } else {
      navigateToCollection('root');
    }
  };

  // Tag management
  const handleUpdateTags = async (document: Document, tags: string[]) => {
    try {
      await updateTagsMutation.mutateAsync({
        documentId: document.id,
        tags,
      });
      toast({
        title: 'Success',
        description: 'Document tags updated successfully',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update document tags',
        variant: 'destructive',
      });
    }
  };

  // Actual file upload logic
  const handleFileUpload = useCallback(
    async (files: File[], investorVisible: boolean) => {
      // Ensure we have a collection to upload to
      let targetCollectionId = currentCollectionId;
      if (!targetCollectionId && entityFolder?.folder_id) {
        targetCollectionId = entityFolder.folder_id;
      }

      if (!targetCollectionId) {
        toast({
          title: 'Error',
          description: 'No folder available for upload',
          variant: 'destructive',
        });
        return;
      }

      const uploadData = {
        files: files,
        collection_id: targetCollectionId,
        entity_associations: [
          {
            entity_type: entityType,
            entity_id: entityId,
            role: 'primary',
            investor_visible: investorVisible,
          },
        ],
        as_of: uploadAsOf || undefined,
      };

      try {
        await uploadMultipleDocumentsMutation.mutateAsync(uploadData);

        // Clear upload progress and close dialog
        setUploadProgress({});
        setShowUploadDialog(false);
        setPendingUploadFiles([]);
        setUploadFileErrors({});
        setUploadInvestorVisible(false);
        setUploadAsOf('');
      } catch (error) {
        console.error('Upload failed:', error);
        // Error toast is handled by the mutation hook. Keep only the rejected
        // files pending - the ones that uploaded must not be sent again - and
        // show each rejection so the user can remove or replace those files.
        const results = (error as BulkUploadError).results;
        if (!results) {
          return;
        }

        const rejected = files
          .map((file, index) => ({ file, result: results[index] }))
          .filter(({ result }) => !result?.document);

        setPendingUploadFiles(rejected.map(({ file }) => file));
        setUploadFileErrors(
          Object.fromEntries(
            rejected.map(({ file, result }) => [
              file.name,
              result?.message ?? 'Upload failed',
            ])
          )
        );
        setShowUploadDialog(true);
      }
    },
    [
      entityType,
      entityId,
      currentCollectionId,
      entityFolder,
      uploadAsOf,
      uploadMultipleDocumentsMutation,
      toast,
    ]
  );

  const handleRemovePendingFile = useCallback((fileName: string) => {
    setPendingUploadFiles((prev) =>
      prev.filter((file) => file.name !== fileName)
    );
    setUploadFileErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([name]) => name !== fileName)
      )
    );
  }, []);

  const handleAddPendingFiles = useCallback((files: FileList | null) => {
    if (!files?.length) return;

    // Copy the FileList now: it is live, and clearing the input's value to allow
    // re-picking the same file empties it before the state updaters below run.
    const selected = Array.from(files);
    const selectedNames = new Set(selected.map((file) => file.name));

    // Re-attaching a file replaces the pending entry with the same name, so a
    // rejected file can be swapped for a corrected one without removing it.
    setPendingUploadFiles((prev) => [
      ...prev.filter((file) => !selectedNames.has(file.name)),
      ...selected,
    ]);
    setUploadFileErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([name]) => !selectedNames.has(name))
      )
    );
  }, []);

  // File upload - for fund type, shows dialog with investor visibility option
  const handleFileSelect = useCallback(
    (files: FileList) => {
      if (onUpload) {
        onUpload(files);
        return;
      }

      const fileArray = Array.from(files);
      setPendingUploadFiles(fileArray);
      setUploadFileErrors({});

      // For fund and portfolio_company entities, show dialog to set investor visibility
      if (
        entityType === EntityType.FUND ||
        entityType === EntityType.PORTFOLIO_COMPANY
      ) {
        setShowUploadDialog(true);
      } else {
        // For other entities (investor), upload directly
        handleFileUpload(fileArray, false);
      }
    },
    [entityType, onUpload, handleFileUpload]
  );

  // Create folder
  const handleCreateFolder = useCallback(async () => {
    if (!folderName.trim()) return;

    if (onCreateFolder) {
      onCreateFolder();
      setFolderName('');
      setShowCreateFolderModal(false);
      return;
    }

    // Ensure we have a parent collection
    let parentCollectionId = currentCollectionId;
    if (!parentCollectionId && entityFolder?.folder_id) {
      parentCollectionId = entityFolder.folder_id;
    }

    try {
      await createCollectionMutation.mutateAsync({
        entityType,
        entityId,
        data: {
          name: folderName,
          description: `Folder for ${entityName}`,
          collection_type: CollectionType.FOLDER,
          parent_collection_id: parentCollectionId ?? undefined,
        },
      });

      setFolderName('');
      setShowCreateFolderModal(false);
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  }, [
    folderName,
    entityType,
    entityId,
    entityName,
    currentCollectionId,
    entityFolder,
    onCreateFolder,
    createCollectionMutation,
  ]);

  const confirmDeleteFolder = useCallback(async () => {
    if (!folderToDelete) return;

    try {
      await deleteCollectionMutation.mutateAsync(folderToDelete.id);
      setShowDeleteFolderDialog(false);
      setFolderToDelete(null);
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  }, [folderToDelete, deleteCollectionMutation]);

  // Document operations
  const handleDocumentClick = (document: Document) => {
    setSelectedDocument(document);
    setShowDocumentViewer(true);
  };

  const handleDownload = (document: Document) => {
    downloadDocumentMutation.mutate({
      id: document.id,
      documentName: document.original_filename,
    });
  };

  const handleDelete = (document: Document) => {
    setDocumentToDelete(document);
    setShowDeleteDocumentDialog(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;

    try {
      await deleteDocumentMutation.mutateAsync(documentToDelete.id);
      setShowDeleteDocumentDialog(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  // Toggle investor visibility for a single document
  const handleToggleInvestorVisibility = async (
    document: Document,
    currentVisibility: boolean
  ) => {
    try {
      await updateInvestorVisibilityMutation.mutateAsync({
        documentId: document.id,
        entityType,
        entityId,
        investorVisible: !currentVisibility,
      });
    } catch (error) {
      console.error('Failed to update visibility:', error);
    }
  };

  // Bulk toggle investor visibility
  const handleBulkToggleVisibility = async (visible: boolean) => {
    if (selectedDocumentIds.size === 0) return;

    try {
      await bulkUpdateVisibilityMutation.mutateAsync({
        entityType,
        entityId,
        documentIds: Array.from(selectedDocumentIds),
        investorVisible: visible,
      });
      setSelectedDocumentIds(new Set());
    } catch (error) {
      console.error('Failed to bulk update visibility:', error);
    }
  };

  // Portal visibility is a document-level master switch (see Document type)
  const getDocumentInvestorVisibility = (document: Document): boolean =>
    document.portal_visible ?? false;

  // Toggle document selection for bulk operations
  const toggleDocumentSelection = (documentId: string) => {
    setSelectedDocumentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(documentId)) {
        newSet.delete(documentId);
      } else {
        newSet.add(documentId);
      }
      return newSet;
    });
  };

  // Select/deselect all documents
  const toggleSelectAll = () => {
    if (selectedDocumentIds.size === documents.length) {
      setSelectedDocumentIds(new Set());
    } else {
      setSelectedDocumentIds(new Set(documents.map((doc) => doc.id)));
    }
  };

  // Drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h3 className="text-lg font-semibold">
                {title || `${entityName} Documents`}
              </h3>
              <p className="text-sm text-muted-foreground">
                {description || `Manage documents and files for ${entityName}`}
              </p>
            </div>
            {currentCollection && (
              <Button
                variant="outline"
                size="sm"
                onClick={navigateUp}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Actions Bar */}
      {showActions && (showUpload || showCreateFolder) && (
        <div className="flex items-center justify-between">
          {/* Bulk actions for fund and portfolio_company entity types */}
          {(entityType === EntityType.FUND ||
            entityType === EntityType.PORTFOLIO_COMPANY) &&
            selectedDocumentIds.size > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  {selectedDocumentIds.size} selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleVisibility(true)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Make Visible
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleVisibility(false)}
                >
                  <EyeOff className="h-4 w-4 mr-2" />
                  Hide
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedDocumentIds(new Set())}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          <div className="flex items-center space-x-2 ml-auto">
            {showCreateFolder && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateFolderModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Folder
              </Button>
            )}
            {showUpload && (
              <Button
                size="sm"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.multiple = true;
                  input.onchange = (e) => {
                    const files = (e.target as HTMLInputElement).files;
                    if (files) {
                      handleFileSelect(files);
                    }
                  };
                  input.click();
                }}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Files
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Breadcrumb Navigation - S3 style */}
      {isInSubfolder && currentCollection && (
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={navigateUp}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">{entityName}</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{currentCollection.name}</span>
        </div>
      )}

      {/* Search */}
      {showSearch && (
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {searchQuery && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <DocumentTagFilter
            selectedTags={filterTags}
            onTagsChange={setFilterTags}
            filterMode={tagFilterMode}
            onFilterModeChange={setTagFilterMode}
          />
        </div>
      )}

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="text-sm font-medium text-blue-800">Uploading...</div>
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="flex items-center space-x-2">
              <div className="flex-1">
                <div className="text-xs text-blue-700">
                  {fileId.split('-')[0]}
                </div>
                <div className="w-full bg-blue-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              <div className="text-xs text-blue-600">{progress}%</div>
            </div>
          ))}
        </div>
      )}

      {/* S3-style unified list - folders and documents */}
      <div className="border rounded-lg overflow-hidden bg-card">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {currentCollection?.name || entityName}
            </span>
            <span className="text-xs text-muted-foreground">
              {subcollections.length > 0 &&
                `${subcollections.length} folder${subcollections.length !== 1 ? 's' : ''}`}
              {subcollections.length > 0 && documents.length > 0 && ', '}
              {documents.length > 0 &&
                `${documents.length} file${documents.length !== 1 ? 's' : ''}`}
            </span>
          </div>
          {(entityType === EntityType.FUND ||
            entityType === EntityType.PORTFOLIO_COMPANY) &&
            documents.length > 0 && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={
                    selectedDocumentIds.size === documents.length &&
                    documents.length > 0
                  }
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-xs text-muted-foreground">
                  Select all
                </span>
              </div>
            )}
        </div>
        {/* Content */}
        <div>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
          {!loading && hasItems && (
            <div
              className="divide-y"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Folders first */}
              {subcollections.map((collection) => (
                <div
                  key={collection.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigateToCollection(collection.id)}
                >
                  <Folder className="h-5 w-5 text-blue-500" />
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToCollection(collection.id);
                      }}
                      className="block w-full text-left font-medium text-sm truncate"
                    >
                      {collection.name}
                    </button>
                    {collection.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {collection.description}
                      </div>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderToDelete({
                            id: collection.id,
                            name: collection.name,
                          });
                          setShowDeleteFolderDialog(true);
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}

              {/* Then documents */}
              {documents.map((document) => {
                const isVisible = getDocumentInvestorVisibility(document);
                return (
                  <div
                    key={document.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => handleDocumentClick(document)}
                  >
                    {/* Checkbox for bulk selection (fund and portfolio_company types) */}
                    {(entityType === EntityType.FUND ||
                      entityType === EntityType.PORTFOLIO_COMPANY) && (
                      <input
                        type="checkbox"
                        checked={selectedDocumentIds.has(document.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleDocumentSelection(document.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    )}
                    <FileText className="h-5 w-5 text-gray-500" />
                    <div className="flex-1 min-w-0">
                      <DocumentPreview document={document}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDocumentClick(document);
                          }}
                          className="block w-full text-left font-medium text-sm truncate hover:text-blue-600 transition-colors"
                        >
                          {document.name}
                        </button>
                      </DocumentPreview>
                      {document.tags.length > 0 && (
                        <div className="mt-0.5">
                          <DocumentTagsList
                            tags={document.tags}
                            size="sm"
                            maxDisplay={2}
                            onTagClick={(tag) => {
                              if (!filterTags.includes(tag)) {
                                setFilterTags([...filterTags, tag]);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      As of {formatDate(document.effective_as_of)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(document.file_size)}
                    </span>
                    {/* Hidden badge - only shown when document is hidden from investors */}
                    {(entityType === EntityType.FUND ||
                      entityType === EntityType.PORTFOLIO_COMPANY) &&
                      !isVisible && (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-gray-100 text-gray-600"
                        >
                          <EyeOff className="h-3 w-3 mr-1" />
                          Hidden
                        </Badge>
                      )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(document);
                          }}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingDocumentTags(document);
                            setSelectedTags(document.tags);
                          }}
                        >
                          <Tag className="h-4 w-4 mr-2" />
                          Edit Tags
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingAsOfDocument(document);
                          }}
                        >
                          <CalendarDays className="h-4 w-4 mr-2" />
                          Edit As of Date
                        </DropdownMenuItem>
                        {/* Toggle investor visibility (fund and portfolio_company types) */}
                        {(entityType === EntityType.FUND ||
                          entityType === EntityType.PORTFOLIO_COMPANY) && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleInvestorVisibility(
                                document,
                                isVisible
                              );
                            }}
                          >
                            {isVisible ? (
                              <>
                                <EyeOff className="h-4 w-4 mr-2" />
                                Hide from Investors
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4 mr-2" />
                                Show to Investors
                              </>
                            )}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(document);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
          {!loading && !hasItems && (
            <div
              className="text-center py-12 mx-4 mb-4 border-2 border-dashed border-gray-300 rounded-lg"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <FileText className="h-12 w-12 mx-auto text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No items
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {showUpload
                  ? 'Upload files or create a folder to get started'
                  : 'This folder is empty'}
              </p>
            </div>
          )}
        </div>
      </div>

      <InvestorVisibilityUploadDialog
        open={showUploadDialog}
        onOpenChange={(open) => {
          setShowUploadDialog(open);
          if (!open) {
            setPendingUploadFiles([]);
            setUploadFileErrors({});
            setUploadInvestorVisible(false);
            setUploadAsOf('');
          }
        }}
        files={pendingUploadFiles}
        fileErrors={uploadFileErrors}
        visibilityLabel={visibilityLabels.toggleLabel}
        visibilityDescription={visibilityLabels.toggleDescription}
        investorVisible={uploadInvestorVisible}
        onInvestorVisibleChange={setUploadInvestorVisible}
        asOf={uploadAsOf}
        onAsOfChange={setUploadAsOf}
        onAddFiles={handleAddPendingFiles}
        onRemoveFile={handleRemovePendingFile}
        onUpload={() =>
          handleFileUpload(pendingUploadFiles, uploadInvestorVisible)
        }
        isUploading={uploadMultipleDocumentsMutation.isPending}
      />

      {editingAsOfDocument && (
        <EditAsOfDialog
          key={editingAsOfDocument.id}
          document={editingAsOfDocument}
          onClose={() => setEditingAsOfDocument(null)}
        />
      )}

      {/* Create Folder Modal */}
      <Dialog
        open={showCreateFolderModal}
        onOpenChange={(open) => {
          setShowCreateFolderModal(open);
          if (!open) setFolderName('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Enter a name for the new folder.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Folder name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && folderName.trim()) {
                  handleCreateFolder();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateFolderModal(false);
                setFolderName('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} disabled={!folderName.trim()}>
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation Dialog */}
      <Dialog
        open={showDeleteFolderDialog}
        onOpenChange={(open) => {
          setShowDeleteFolderDialog(open);
          if (!open) setFolderToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Folder</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{folderToDelete?.name}
              &rdquo;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteFolderDialog(false);
                setFolderToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteFolder}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <Dialog
        open={showDeleteDocumentDialog}
        onOpenChange={(open) => {
          setShowDeleteDocumentDialog(open);
          if (!open) setDocumentToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;
              {documentToDelete?.original_filename}&rdquo;? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDocumentDialog(false);
                setDocumentToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteDocument}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tags Dialog */}
      <Dialog
        open={!!editingDocumentTags}
        onOpenChange={(open) => {
          if (!open) {
            setEditingDocumentTags(null);
            setSelectedTags([]);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document Tags</DialogTitle>
            <DialogDescription>
              Update tags for &ldquo;
              {editingDocumentTags?.original_filename ||
                editingDocumentTags?.name}
              &rdquo;
            </DialogDescription>
          </DialogHeader>
          {editingDocumentTags && (
            <div className="py-4">
              <DocumentTagPickerSimple
                value={selectedTags}
                onChange={setSelectedTags}
                placeholder="Search tags..."
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingDocumentTags(null);
                setSelectedTags([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (editingDocumentTags) {
                  handleUpdateTags(editingDocumentTags, selectedTags);
                  setEditingDocumentTags(null);
                  setSelectedTags([]);
                }
              }}
            >
              Save Tags
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Modal */}
      <DocumentViewer
        document={selectedDocument}
        isOpen={showDocumentViewer}
        onClose={() => {
          setShowDocumentViewer(false);
          setSelectedDocument(null);
        }}
      />
    </div>
  );
}
